// Design v7 (TaskCool v7.html, changelog 7.0) helpers kept out of the view-model port: project icons, comment
// notifications, the comments/history timeline, the desktop screen saver, profile-photo cropping and note-image
// downscaling. Behaviour follows the reference logic (design/unpacked/task-panes-v7/logic.split.js); see
// design/inventory-v7.md for line references and the documented production differences.
import iconSet from '@tokens/project-icons.json';
import type { Comment, LogEntry, Project, Task, TaskList } from '@/domain/model';
import { projectCommentNotifs } from './v9';

// ── Project icons (reference PICONS / ICATS / picon) ────────────────────────────────────────────────────────
export const PICONS = iconSet.icons as Record<string, string>;
export const ICATS: Array<[string, string[]]> = iconSet.categories.map(c => [c.name, c.keys]);
export const ICON_COUNT = Object.keys(PICONS).length;
/** CSS mask image of an icon (single colour, drawn with `background: currentColor`), or 'none' for no/unknown icon. */
export function picon(k: string | null | undefined): string {
  if (!k || !PICONS[k]) return 'none';
  const svg = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="black" stroke-width="' + iconSet.stroke.width + '" stroke-linecap="round" stroke-linejoin="round">' + PICONS[k] + '</svg>';
  return 'url("data:image/svg+xml,' + encodeURIComponent(svg) + '")';
}
/** Icon keys shown by the picker: null = "Letters (default)"; a query searches every icon, else the category. */
export function iconKeys(q: string, cat: string): Array<string | null> {
  const iq = q.trim().toLowerCase();
  const inCat = cat === 'All' ? null : new Set((ICATS.find(c => c[0] === cat) || ['', []])[1]);
  return [null, ...Object.keys(PICONS)].filter(k => (!iq || (k ? k : 'letters').includes(iq)) && (iq || !inCat || (k !== null && inCat.has(k))));
}
export const iconLabel = (k: string): string => k[0]!.toUpperCase() + k.slice(1);

// ── Notifications: comments on my tasks or on tasks I commented on (reference myNotifs) ─────────────────────
export interface NotifRow { p: Project; l: TaskList; t: Task; c: Comment; why: 'on your task' | 'on a task you commented on' | 'on the project' }
export function myNotifs(visible: Project[], me: string): NotifRow[] {
  const rows: NotifRow[] = [];
  for (const p of visible) for (const l of p.lists) for (const t of l.tasks) {
    const cs = t.comments || []; if (!cs.length) continue;
    const cr = (t.log || []).find(e => e.k === 'create'), mine = !!cr && cr.by === me;
    for (const c of cs) {
      if (c.by === me) continue;
      const joined = cs.some(x => x.by === me && x.at < c.at);
      if (mine || joined) rows.push({ p, l, t, c, why: mine ? 'on your task' : 'on a task you commented on' });
    }
  }
  rows.push(...projectCommentNotifs(visible, me)); // v9: comments on projects I'm a member of
  return rows.sort((a, b) => b.c.at - a.c.at);
}

// ── Comments ───────────────────────────────────────────────────────────────────────────────────────────────
/** Splits comment text into plain and @username parts (a part is highlighted only for a known username). */
export function commentParts(text: string, usernames: Set<string>): Array<{ text: string; isAt: boolean; isTxt: boolean }> {
  const parts: Array<{ t: string; at: boolean }> = [];
  const re = /(@[a-z0-9_.]+)/gi; let last = 0, m: RegExpExecArray | null;
  while ((m = re.exec(text))) {
    if (m.index > last) parts.push({ t: text.slice(last, m.index), at: false });
    parts.push({ t: m[0], at: usernames.has(m[0].slice(1).toLowerCase()) });
    last = m.index + m[0].length;
  }
  if (last < text.length) parts.push({ t: text.slice(last), at: false });
  return parts.map(p => ({ text: p.t, isAt: p.at, isTxt: !p.at }));
}
/** History entries shown in the timeline: everything except the comment markers themselves. */
export const isSystemEntry = (e: LogEntry): boolean => e.k !== 'comment' && !(e.k === 'mention' && /in a comment$/.test(e.text || ''));

// ── Screen saver (reference setupSaver / saverEnabled, L409–427) ───────────────────────────────────────────
export interface SaverHost {
  enabled(): boolean;
  isOn(): boolean; isHover(): boolean; hoverIndex(): number | null; days(): number;
  show(): void; hide(): void; hover(i: number): void;
}
/** Wires the idle timer: 10 s without input shows the saver; pointer over the chart (lower 40 %) keeps it. */
export function setupSaver(h: SaverHost): () => void {
  let idleT: ReturnType<typeof setTimeout> | undefined, graceT: ReturnType<typeof setTimeout> | null = null;
  // v9: never while typing in a text field — the idle timer re-arms instead.
  const typing = () => { const a = document.activeElement as HTMLElement | null; return !!a && (a.tagName === 'TEXTAREA' || (a.tagName === 'INPUT' && !/^(checkbox|radio|button|range|file)$/.test((a as HTMLInputElement).type)) || a.isContentEditable); };
  const arm = () => { clearTimeout(idleT); idleT = setTimeout(() => { if (typing()) { arm(); return; } if (h.enabled() && !document.hidden) h.show(); }, 10_000); };
  const dismiss = () => { if (graceT) clearTimeout(graceT); graceT = null; h.hide(); arm(); };
  const overChart = (e: Event) => typeof (e as MouseEvent).clientY === 'number' && (e as MouseEvent).clientY > window.innerHeight * 0.6;
  const wake = (e?: Event) => {
    if (!h.isOn()) { arm(); return; }
    const t = e && e.type;
    if (t === 'keydown' || t === 'input' || t === 'wheel' || t === 'touchstart') { dismiss(); return; }
    if (t === 'mousedown') { if (h.isHover() && overChart(e!)) return; dismiss(); return; }
    if (t === 'mousemove') {
      if (overChart(e!)) {
        if (graceT) clearTimeout(graceT); graceT = null;
        const n = h.days(), i = Math.max(0, Math.min(n - 1, Math.round((e as MouseEvent).clientX / window.innerWidth * (n - 1))));
        if (!h.isHover() || h.hoverIndex() !== i) h.hover(i);
        return;
      }
      if (h.isHover()) { dismiss(); return; }
      if (!graceT) graceT = setTimeout(() => { graceT = null; if (h.isOn() && !h.isHover()) dismiss(); }, 1200);
      return;
    }
    dismiss();
  };
  const evs = ['mousemove', 'mousedown', 'keydown', 'input', 'wheel', 'touchstart'] as const;
  evs.forEach(ev => window.addEventListener(ev, wake, { passive: true, capture: true }));
  wake();
  return () => { evs.forEach(ev => window.removeEventListener(ev, wake, { capture: true })); clearTimeout(idleT); if (graceT) clearTimeout(graceT); };
}
/** Desktop only, never on coarse pointers or narrow windows; on unless turned off in Profile; reduced motion keeps it (fades only). */
export function saverAllowed(pref: unknown, mobileView: boolean): boolean {
  const coarse = typeof window !== 'undefined' && !!window.matchMedia && window.matchMedia('(pointer: coarse)').matches;
  return (pref === undefined || pref === null ? true : !!pref) && !mobileView && window.innerWidth > 700 && !coarse;
}

// ── Images: profile photo crop and note-image downscale ────────────────────────────────────────────────────
/** Thrown when this browser cannot decode the picked file (e.g. HEIC photos in Chrome). */
export class UnsupportedImageError extends Error {}
function loadImage(file: Blob): Promise<HTMLImageElement | ImageBitmap> {
  const viaImg = () => new Promise<HTMLImageElement>((resolve, reject) => {
    const url = URL.createObjectURL(file), img = new Image();
    img.onload = () => { URL.revokeObjectURL(url); resolve(img); };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new UnsupportedImageError('not an image')); };
    img.src = url;
  });
  // createImageBitmap honours EXIF orientation and decodes more formats in some browsers; <img> is the fallback.
  if (typeof createImageBitmap === 'function') return createImageBitmap(file, { imageOrientation: 'from-image' }).catch(viaImg);
  return viaImg();
}
const toBlob = (c: HTMLCanvasElement, type: string, q: number) => new Promise<Blob>((res, rej) => c.toBlob(b => (b ? res(b) : rej(new Error('encode failed'))), type, q));
/** Reference onPhotoFile: centre square crop, 256 × 256 JPEG at quality 0.85. */
export async function cropAvatar(file: Blob, size = 256): Promise<Blob> {
  const img = await loadImage(file), k = Math.min(img.width, img.height);
  const c = document.createElement('canvas'); c.width = c.height = size;
  c.getContext('2d')!.drawImage(img, (img.width - k) / 2, (img.height - k) / 2, k, k, 0, 0, size, size);
  return toBlob(c, 'image/jpeg', 0.85);
}
/** Reference addNoteImages: longest side ≤ 1400 px, re-encoded as JPEG 0.85 when scaled or large; returns the aspect ratio. */
export async function prepareNoteImage(file: File, max = 1400): Promise<{ file: File; ar: number }> {
  const img = await loadImage(file), ar = img.width / Math.max(1, img.height);
  const k = Math.min(1, max / Math.max(img.width, img.height));
  if (k >= 1 && file.size <= 600_000) return { file, ar };
  const c = document.createElement('canvas'); c.width = Math.round(img.width * k); c.height = Math.round(img.height * k);
  c.getContext('2d')!.drawImage(img, 0, 0, c.width, c.height);
  const blob = await toBlob(c, 'image/jpeg', 0.85);
  const name = (file.name || 'image').replace(/\.[a-z0-9]+$/i, '') + '.jpg';
  return { file: new File([blob], name, { type: 'image/jpeg' }), ar };
}
/** Thumbnail box like the reference (one image: 160 px high, up to 300 px wide; several: 96 px, up to 160 px). */
export function thumbSize(count: number, ar: number | undefined): { w: string; h: string } {
  const h = count === 1 ? 160 : 96, w = Math.round(Math.max(72, Math.min(count === 1 ? 300 : 160, h * (ar || 1.4))));
  return { w: w + 'px', h: h + 'px' };
}
